import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function bufToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) out += String.fromCharCode(bytes[i]!);
  return btoa(out);
}

export function usePush() {
  const qc = useQueryClient();
  const vapid = useQuery({
    queryKey: keys.pushVapid(),
    queryFn: () => api<{ enabled: boolean; publicKey: string | null }>("/api/push/vapid-public-key"),
    staleTime: Infinity,
  });
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const [subscribed, setSubscribed] = useState(false);

  async function refresh() {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(Boolean(sub));
      if (typeof Notification !== "undefined") setPermission(Notification.permission);
    } catch {
      setSubscribed(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const enable = useMutation({
    mutationFn: async () => {
      if (!vapid.data?.enabled || !vapid.data.publicKey) throw new Error("Push is not configured");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Notification permission denied");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.data.publicKey),
      });
      await api("/api/push/subscribe", {
        method: "POST",
        body: {
          endpoint: sub.endpoint,
          keys: {
            p256dh: bufToBase64(sub.getKey("p256dh")!),
            auth: bufToBase64(sub.getKey("auth")!),
          },
        },
      });
      setSubscribed(true);
    },
    onSuccess: () => invalidateAfter(qc, "pushChanged"),
  });

  const disable = useMutation({
    mutationFn: async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api(`/api/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`, { method: "DELETE" });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    },
  });

  return { enabled: Boolean(vapid.data?.enabled), permission, subscribed, enable, disable, refresh };
}

